import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import AddictionService from "./AddictionService";
import { addictionBand, formatDuration } from "./data";
import { ADD_V2_REFRESH } from "./events";
import ModalPortal from "../common/ModalPortal";
import MetaTooltip from "../medicines/MetaTooltip";

/**
 * "View trend" modal for one addiction — the same shell as the allergy
 * trend modal, columns adapted to the addiction schema:
 *
 *   TIMESTAMP · STARTED WHEN · FREQUENCY · TOTAL INTERVAL · DESCRIPTION ·
 *   REPORTED BY · STATUS · flag
 *
 * The most-recent history row IS the live addiction — its flag column
 * stays empty (no button, no chip); the backend rejects a mark on the
 * live row with 400. Rows are rendered newest-first. Marked-incorrect
 * rows are struck-through / dimmed with a red flag; clicking the red
 * flag opens the marked-incorrect audit dialog (BY/ON/AT — no undo).
 */

// "27 Jul 2026\n01:15 pm"
const formatTimestamp = (iso) => {
  if (!iso) return { date: "—", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: "—", time: "" };
  return {
    date: d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: d
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase(),
  };
};

const formatTimeOnly = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
};

/* Board 4143:8793 — a SOLID filled pennant with a thin pole, not an outline.
   Figma issues it as the Material Icons `flag` ligature; Material Icons is not
   loaded in this app, so it is redrawn here as the equivalent inline path
   (there is precedent for this in the deleted-entries close glyph). */
const FlagIcon = ({ marked }) => (
  <svg
    viewBox="0 0 24 24"
    fill={marked ? "#ef4444" : "#98a2b3"}
    aria-hidden="true"
  >
    <path d="M14.4 6l-.4-2H5v17h2v-7h5.6l.4 2h7V6z" />
  </svg>
);
FlagIcon.propTypes = { marked: PropTypes.bool };

/* Board 4143:8765 — an 82x22 radius-4 chip. Both the leading dot and the
   trailing caret are authored `hidden` on every instance, so neither is
   rendered here; the shape lives in CSS and only the band colours are inline.

   A marked row dims the WHOLE chip to 55% and recolours its label, and both
   have to be inline because the band colours are: a class rule for the label
   would lose to the inline `color` this same element carries. */
const StatusPill = ({ status, marked }) => {
  const band = addictionBand(status || "never");
  return (
    <span
      className="alg-trend-status"
      style={{
        background: band.bg,
        color: marked ? "#98a2b3" : band.fg,
        opacity: marked ? 0.55 : undefined,
      }}
    >
      {band.label}
    </span>
  );
};
StatusPill.propTypes = { status: PropTypes.string, marked: PropTypes.bool };

/* Board 4143:8725 issues the TREND modal's close differently from the three
   dialogs': a Material Icons `close` LIGATURE at 16px / lh 16 / #6a7282 —
   a filled X with squared arms — where 8842/8855/8870 ship a real vector
   asset (a 14px 1.5-stroke X in #4a5565). Two genuinely different controls,
   so they get two glyphs. Material Icons is not loaded in this app, hence
   the inline path. */
const MaterialCloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/* Boards 4143:8842 / 8855 / 8870 all issue THEIR close as the same real
   vector asset: a 14x14 X inset 7 in a 28 box, 1.5 stroke, round caps, in
   #4a5565. Distinct from the trend modal's Material glyph above. */
const CloseIcon = () => (
  <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
    <path
      d="M0.75 0.75L13.25 13.25M13.25 0.75L0.75 13.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// —— Review dialog (unmarked → offer Mark as incorrect) ————————————
/* Board 4143:8842 · "Popup · Make a correction?" — 420x156, three full-bleed
   58/40/58 bands with no padding on the card itself.

   The frame name and the board's own caption ("choose: correct the entry, or
   mark it incorrect") promise TWO outcomes, but the frame as drawn has exactly
   one button and AddictionService exposes no endpoint to edit a history row —
   only markHistoryIncorrect. The drawn frame and the code already agree at one
   action, so no second button is invented here. */
const ReviewEntryDialog = ({ submitting, onCancel, onConfirm }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={submitting ? null : onCancel}>
    <div
      className="alg-review ad3-review"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-review-head">
        <h3 className="alg-review-title">Review this entry</h3>
        <button
          type="button"
          className="alg-review-close"
          onClick={onCancel}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </header>
      <p className="alg-review-body">You can mark the whole entry as incorrect.</p>
      <footer className="alg-review-foot">
        <button
          type="button"
          className="alg-review-mark"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? "Marking…" : "Mark as incorrect"}
        </button>
      </footer>
    </div>
  </div>
);
ReviewEntryDialog.propTypes = {
  submitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

// —— Success card ————————————————————————————————————————————————————
/* Board 4143:8855 · "Popup · Marked as incorrect" — 420x245, and like 8842 it
   is three full-bleed bands (57 header / 129 body / 57 footer) rather than one
   padded block with an absolutely-positioned close. The bands are what put the
   close control in normal flow, so they are real structure, not wrappers.

   This is a DIFFERENT node from 4143:8870 — see MarkedIncorrectDialog. */
const SuccessDialog = ({ onDone }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onDone}>
    <div
      className="alg-success ad3-success"
      onMouseDown={(e) => e.stopPropagation()}
      role="alertdialog"
      aria-modal="true"
    >
      <header className="ad3-success-head">
        <button
          type="button"
          className="alg-review-close"
          onClick={onDone}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </header>
      <div className="ad3-success-body">
        <span className="alg-success-medallion" aria-hidden="true">
          <FlagIcon marked />
        </span>
        <h3 className="alg-success-title">Entry marked as incorrect</h3>
        <p className="alg-success-body">This entry remains in the audit history.</p>
      </div>
      <footer className="ad3-success-foot">
        <button type="button" className="alg-success-done" onClick={onDone}>
          Done
        </button>
      </footer>
    </div>
  </div>
);
SuccessDialog.propTypes = { onDone: PropTypes.func.isRequired };

// —— Marked-incorrect audit (BY / ON / AT) ————————————————————————————
const MarkedIncorrectDialog = ({ row, onClose }) => (
  <div className="cc-modal-backdrop delc-backdrop" onMouseDown={onClose}>
    <div
      className="alg-marked ad3-marked"
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <header className="alg-marked-head">
        <span className="alg-marked-eyebrow">
          <FlagIcon marked /> MARKED INCORRECT
        </span>
        <button
          type="button"
          className="alg-review-close"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </header>
      <dl className="alg-marked-dl">
        <dt>By</dt>
        <dd>
          {row.markedIncorrectByName ||
            (row.markedIncorrectBy &&
              `${row.markedIncorrectBy.who} #${row.markedIncorrectBy.whoId}`) ||
            "—"}
        </dd>
        <dt>On</dt>
        <dd>{row.markedIncorrectAt ? formatShortDate(row.markedIncorrectAt) : "—"}</dd>
        <dt>At</dt>
        <dd>{row.markedIncorrectAt ? formatTimeOnly(row.markedIncorrectAt) : "—"}</dd>
      </dl>
    </div>
  </div>
);
MarkedIncorrectDialog.propTypes = {
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

const TrendModal = ({ patientId, addictionKey, label, onClose }) => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const svc = new AddictionService();
    svc.trend(
      patientId,
      addictionKey,
      (res) => {
        setTrend(res || { history: [] });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load trend");
        setLoading(false);
      }
    );
  }, [patientId, addictionKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const rows = (trend && trend.history) || [];
  const liveIndex =
    rows.length > 0 ? Math.max(...rows.map((r) => r.index)) : -1;
  // Newest-first — matches the same ordering we chose for the allergy
  // trend modal.
  const displayRows = [...rows].sort((a, b) => b.index - a.index);

  const submitMarkIncorrect = () => {
    if (!flagTarget || submitting) return;
    setSubmitting(true);
    const svc = new AddictionService();
    svc.markHistoryIncorrect(
      patientId,
      addictionKey,
      flagTarget.index,
      (trend && trend.version) || 0,
      () => {
        setSubmitting(false);
        setFlagTarget(null);
        setSuccessOpen(true);
        EventService.emit(ADD_V2_REFRESH);
        load();
      },
      (err) => {
        setSubmitting(false);
        setFlagTarget(null);
        // eslint-disable-next-line no-alert
        window.alert((err && err.message) || "Mark failed — please retry");
      }
    );
  };

  const flagIsMarked = flagTarget && flagTarget.markedIncorrect;

  return (
    <ModalPortal>
      <div className="cc-modal-backdrop" onMouseDown={submitting ? null : onClose}>
        <div
          className="cc-modal alg-trend ad3-trend"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-trend-title"
        >
          <header className="alg-trend-head">
            <h2 id="ad-trend-title" className="alg-trend-title">
              {(label || "").toUpperCase()}
            </h2>
            <button
              type="button"
              className="alg-trend-close"
              onClick={onClose}
              aria-label="Close"
            >
              <MaterialCloseIcon />
            </button>
          </header>
  
          {/* Board 4143:8728 — the toolbar and the table sit inside ONE
              bordered, rounded, clipped card on a tinted body. They were loose
              children of the modal. */}
          <div className="ad3-trend-body">
            <div className="ad3-trend-card">
          <div className="alg-trend-sub">
            <span className="alg-trend-tab">Audit</span>
            {/* No leading middot on the board — the toolbar's 10px gap is the
                whole separation. The entry/entries switch is ours; the board
                only ever shows the plural. */}
            <span className="alg-trend-count">
              {rows.length} {rows.length === 1 ? "entry" : "entries"}
            </span>
          </div>
  
          {loading && <div className="alg-trend-empty">Loading trend…</div>}
          {error && <div className="alg-trend-empty is-error">{error}</div>}
  
          {!loading && !error && rows.length === 0 && (
            <div className="alg-trend-empty">No audit history yet.</div>
          )}
  
          {!loading && !error && rows.length > 0 && (
            <div className="alg-trend-table" role="table">
              <div className="alg-trend-thead" role="row">
                <span role="columnheader">Timestamp</span>
                <span role="columnheader">Started when</span>
                <span role="columnheader">Frequency</span>
                <span role="columnheader">Total interval</span>
                <span role="columnheader">Description</span>
                <span role="columnheader">Reported by</span>
                <span role="columnheader">Status</span>
                <span role="columnheader" aria-label="Actions" />
              </div>
  
              {displayRows.map((r) => {
                const ts = formatTimestamp(r.recordedAt);
                const isMarked = !!r.markedIncorrect;
                const isLive = r.index === liveIndex;
                return (
                  <div
                    className={`alg-trend-row${isMarked ? " is-marked" : ""}${isLive ? " is-live" : ""}`}
                    role="row"
                    key={r.index}
                  >
                    <span className="alg-trend-cell alg-trend-ts" role="cell">
                      {/* Board 4143:8752 — `spacer · chev`, a 12px empty frame
                          that puts the text at x=24. Its NAME reserves room for
                          a row-expander chevron, but the board draws none and
                          these rows do not expand, so it stays inert. */}
                      <span className="ad3-ts-spacer" aria-hidden="true" />
                      <span className="ad3-ts-lines">
                        <span>{ts.date}</span>
                        {ts.time && <span className="alg-trend-ts-time">{ts.time}</span>}
                      </span>
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {formatDuration(r.startedWhen)}
                    </span>
                    <span className="alg-trend-cell alg-trend-desc" role="cell">
                      {/* Same 1-line clamp + hover-when-truncated tooltip
                          treatment Description uses, so long free-text
                          frequencies ("2 puffs after every meal…") don't
                          get silently cut. */}
                      <MetaTooltip text={r.frequency || "—"} lines={1} />
                    </span>
                    {/* Bare, not `~…` — the board tildes STARTED WHEN only. */}
                    <span className="alg-trend-cell" role="cell">
                      {formatDuration(r.totalInterval, false)}
                    </span>
                    <span
                      className="alg-trend-cell alg-trend-desc"
                      role="cell"
                    >
                      {/* 3-line clamp + white hover tooltip; only reveals
                          when the text is actually being truncated. Same
                          pattern the section table uses. */}
                      <MetaTooltip text={r.description || ""} lines={3} />
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      {r.reportedByName ||
                        (r.reportedBy &&
                          `${r.reportedBy.who} #${r.reportedBy.whoId}`) ||
                        "—"}
                    </span>
                    <span className="alg-trend-cell" role="cell">
                      <StatusPill status={r.newStatus} marked={isMarked} />
                    </span>
                    <span className="alg-trend-cell alg-trend-flag-cell" role="cell">
                      {!isLive && (
                        <button
                          type="button"
                          className="alg-trend-flag"
                          onClick={() => setFlagTarget(r)}
                          aria-label={isMarked ? "View incorrect-mark audit" : "Review this entry"}
                          title={isMarked ? "View incorrect-mark audit" : "Review this entry"}
                        >
                          <FlagIcon marked={isMarked} />
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
            </div>
          </div>
        </div>
  
        {flagTarget && !flagIsMarked && !successOpen && (
          <ReviewEntryDialog
            submitting={submitting}
            onCancel={() => setFlagTarget(null)}
            onConfirm={submitMarkIncorrect}
          />
        )}
        {flagTarget && flagIsMarked && (
          <MarkedIncorrectDialog
            row={flagTarget}
            onClose={() => setFlagTarget(null)}
          />
        )}
        {successOpen && <SuccessDialog onDone={() => setSuccessOpen(false)} />}
      </div>
    </ModalPortal>
  );
};

TrendModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  addictionKey: PropTypes.string.isRequired,
  label: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default TrendModal;
